/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import type { PluginManager, ScopeName } from '@opengeoweb/plugin-interface';
import type { ConfigType } from '@opengeoweb/shared';
import type { syncGroupsTypes } from '@opengeoweb/store';
import { genericActions, syncConstants } from '@opengeoweb/store';
import { usePluginToRedux } from './usePluginToRedux';
import { useReduxToPlugin } from './useReduxToPlugin';

interface UsePluginReduxBridgeOptions {
  pluginBridge: PluginManager;
  sourceId: string;
  scopes?: string[];
  applicationConfig?: ConfigType;
}

export const usePluginReduxBridge = ({
  pluginBridge,
  sourceId,
  scopes,
  applicationConfig,
}: UsePluginReduxBridgeOptions): void => {
  const dispatch = useDispatch();

  const hasScope = useCallback(
    (scope: ScopeName): boolean => (!scopes ? true : scopes.includes(scope)),
    [scopes],
  );
  const syncGroupsAddSource = useCallback(
    (payload: syncGroupsTypes.SyncGroupsAddSourcePayload): void => {
      dispatch(genericActions.syncGroupAddSource(payload));
    },
    [dispatch],
  );
  const syncGroupRemoveSource = useCallback(
    (payload: syncGroupsTypes.SyncGroupRemoveSourcePayload): void => {
      dispatch(genericActions.syncGroupRemoveSource(payload));
    },
    [dispatch],
  );

  useEffect(() => {
    if (hasScope('time')) {
      syncGroupsAddSource({
        id: sourceId,
        type: [syncConstants.SYNCGROUPS_TYPE_SETTIME],
      });
    }
    if (hasScope('features')) {
      syncGroupsAddSource({
        id: sourceId,
        type: [syncConstants.SYNCGROUPS_TYPE_SETFEATURES],
      });
    }

    return (): void => {
      syncGroupRemoveSource({ id: sourceId });
    };
  }, [syncGroupsAddSource, syncGroupRemoveSource, sourceId, hasScope]);

  useReduxToPlugin({ pluginBridge, sourceId, hasScope, applicationConfig });
  usePluginToRedux({ pluginBridge, sourceId, hasScope });
};
